import type {
  DisruptionDetail,
  DisruptionProgress,
  ResolutionState,
} from '@/lib/disruptions/types';
import { causeLabel, formatDateTime, formatPriceMinor } from '../labels';
import { stepLabel, stepState } from '../resolution-steps';
import type { JournalLine } from './tower-journal';
import { bundleOf, factsOf, STEP_ORDER, stepOf, transfersOf } from './tower-model';
import { towerT, type TowerT } from './tower-i18n';

// “Copy shift summary” — plain text for the handoff, composed CLIENT-SIDE from
// resolution-state + the live snapshot + this session's log. No notes backend, no
// new operator-authored state (phase 3 persists a server feed).

export function buildShiftSummary(
  detail: DisruptionDetail,
  state: ResolutionState,
  live: DisruptionProgress | undefined,
  lines: JournalLine[],
  t: TowerT = towerT,
): string {
  const g = factsOf(state);
  const out: string[] = [];
  out.push(t('summary.title', { title: detail.title }));
  out.push(
    `${t('summary.header', {
      cause: causeLabel(detail.cause, t),
      status: detail.status,
      detectedAt: formatDateTime(detail.detectedAt),
    })}${detail.containedAt ? t('summary.contained', { at: formatDateTime(detail.containedAt) }) : ''}`,
  );
  out.push(
    t('summary.counts', {
      flights: state.kpis.caseCount,
      groupsPart: g ? t('summary.groupsPart', { count: g.groups }) : '',
      pax: state.kpis.affectedPassengers,
      // airline-money@v2 — the airline's PRICE; null → «No disponible».
      price: formatPriceMinor(state.kpis.totalPriceMinor, state.kpis.currency, t),
    }),
  );
  if (g) {
    const b = bundleOf(g);
    out.push(
      `${t('summary.offers', {
        offered: b.offered,
        ready: b.offerReady,
        flightOnly: b.flightOnly,
        accepted: g.accepted,
        declined: g.declined,
        awaiting: g.awaiting,
        issued: b.issued,
      })}${b.commitFailed ? t('summary.offersFailed', { count: b.commitFailed }) : ''}`,
    );
  }
  const tf = transfersOf(state, live);
  if (tf && tf.known && (tf.hasPlan || tf.needed > 0)) {
    const ret = tf.returnScheduled + tf.returnOnRoad + tf.returnDone;
    out.push(
      `${t('summary.transfers', {
        arrived: tf.paxArrived,
        toMove: tf.paxToMove,
        hotels: tf.hotels,
        vehicles: tf.vehiclesTotal,
        onRoad: tf.vehiclesOnRoad,
        noShow: tf.noShow,
        ownMeans: tf.ownMeans,
        failed: tf.failed,
      })}${ret > 0 ? t('summary.transfersReturn', { done: tf.returnDone, total: ret }) : ''}${
        state.kpis.transportPriceMinor !== undefined
          ? t('summary.transfersPrice', {
              price: formatPriceMinor(state.kpis.transportPriceMinor, state.kpis.currency, t),
            })
          : ''
      }`,
    );
  }
  out.push('');
  out.push(t('summary.steps'));
  for (const op of STEP_ORDER) {
    const s = stepOf(state, op);
    if (!s) continue;
    const st = stepState(s, state.kpis.groups ?? null, state.steps);
    const counter = s.done != null && s.total != null ? ` ${s.done}/${s.total}` : '';
    out.push(`- ${stepLabel(op)}: ${st.label}${counter}${st.detail ? ` — ${st.detail}` : ''}`);
  }
  out.push('');
  out.push(
    t('summary.next', {
      label: state.nextStep.label,
      cta: state.nextStep.cta ? ` → ${state.nextStep.cta}` : '',
      reason: state.nextStep.reason,
    }),
  );
  const p = state.problems;
  const residual: string[] = [];
  const noOptions = Object.entries(p.noOptionsByReason ?? {}).filter(([, n]) => n > 0);
  if (noOptions.length)
    residual.push(
      t('summary.residue.noOptions', {
        list: noOptions.map(([k, n]) => `${k} ${n}`).join(', '),
      }),
    );
  if (p.degradedGroups) residual.push(t('summary.residue.degraded', { count: p.degradedGroups }));
  if (p.allocationGaps)
    residual.push(t('summary.residue.allocationGaps', { count: p.allocationGaps }));
  if (p.declined) residual.push(t('summary.residue.declined', { count: p.declined }));
  if (p.awaitingAcceptance)
    residual.push(t('summary.residue.awaiting', { count: p.awaitingAcceptance }));
  if (p.offersNotSent)
    residual.push(t('summary.residue.offersNotSent', { count: p.offersNotSent }));
  if (p.acceptedNotIssued)
    residual.push(t('summary.residue.acceptedNotIssued', { count: p.acceptedNotIssued }));
  if (p.commitFailed) residual.push(t('summary.residue.commitFailed', { count: p.commitFailed }));
  if (p.transferNoShow)
    residual.push(t('summary.residue.transferNoShow', { count: p.transferNoShow }));
  if (p.transferFailed)
    residual.push(t('summary.residue.transferFailed', { count: p.transferFailed }));
  if (p.transferLate) residual.push(t('summary.residue.transferLate', { count: p.transferLate }));
  if (p.returnUnscheduled)
    residual.push(t('summary.residue.returnUnscheduled', { count: p.returnUnscheduled }));
  if (residual.length) {
    out.push('');
    out.push(t('summary.residue.title', { list: residual.join(' · ') }));
  }
  if (live?.activeRun) {
    out.push('');
    out.push(
      t('summary.inProgress', {
        op: live.activeRun.op,
        operator: live.activeRun.operatorUrn,
        since: formatDateTime(live.activeRun.startedAt),
      }),
    );
  }
  if (lines.length) {
    out.push('');
    out.push(t('summary.log'));
    for (const l of [...lines].reverse()) {
      out.push(
        `${new Date(l.at).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })} ${l.text}`,
      );
    }
  }
  out.push('');
  out.push(t('summary.footer', { urn: detail.urn, at: new Date().toLocaleString() }));
  return out.join('\n');
}
